import React, { useEffect, useRef, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import {
  downloadTablePNG,
  formatLocalizedDate,
  getWeeksBetween,
  transformGraphData,
  transformHeatMapData,
} from "../../../../../common/utils/utils";
import HeatMapsChart from "../../../../../components/chart/HeatMapsChart";
import TreeMapChart from "../../../../../components/chart/TreeMapChart";
import PolarChart from "../../../../../components/chart/PolarChart";
import DonutChart from "../../../../../components/chart/DonutChart";
import { GRAPH_FILL_COLOR } from "../constant/graph-color-scheme";
import PieChart from "../../../../../components/chart/PieChart";
import CommonTableLoader from "../../../../../skeleton/common-table-loader";
import PageNotFound from "../../../../PageNotFound";
import ReactTable from "../../../../../lib/table/react-table";
import { findIcon } from "../../../../dashboard/icons";
import { fallbackImages } from "../../../../../utilities/fallback-images";
import useCsvDownloader from "../../../../../components/custom-hooks/use-csv-downloader";
import SparlLineBar from "../../../../../components/chart/SparlLineBar";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  useGetAudienceDaywisePerformance,
  useGetAudienceZonalDistribution,
  useGetAudienceViewsByScreensize,
  useGetAudienceDeviceDistribution,
  useGetAudienceSpecialityBreakup,
  useGetAudienceSpecialitiesReport,
  useGetSummaryList,
  useGetAudienceSpecialitiesReportCTA,
} from "../../../../../queries/query-hooks/workspace/sponsored-content-hook";
import SquareChartSkeleton from "../../../../../skeleton/workspace/clirnet/brand-ads/square-chart-skeleton";
import { useGetProjectDetails } from "../../../../../queries/query-hooks/workspace/workspace-hook";
import { checkProjectAccess } from "../utils/common";
import { ProjectHeaderTopBar, TableHeader, CardNotFound } from "@ui";
import ProjectFilters from "../components/ProjectFilters";
import { useDispatch, useSelector } from "react-redux";
import { selectProject } from "store/project/project.selector";
import { updateProject } from "store/project/project.action";

function Audience() {
  const [tableColumnData, setTableColumnData] = useState([]);
  const dispatch = useDispatch();
  const project = useSelector(selectProject);
  const { project_id } = useParams();
  const { downloadCSV } = useCsvDownloader();
  const [analysisType, setAnalysisType] = useState("week");

  const { data: projectDetails, isLoading: isProjectDetailsLoading } =
    useGetProjectDetails(project_id);

  const [dateRangePayload, setDateRangePayload] = useState({
    start_date: "",
    end_date: "",
  });
  const [isProjectChange, setIsProjectChange] = useState(false);

  const [activeCampaignGroup, setActiveCampaignGroup] = useState({});

  useEffect(() => {
    setIsProjectChange(true);
  }, [project_id]);

  const { data: summaryList, isLoading: isSummaryListLoading } =
    useGetSummaryList(
      project_id,
      {
        start_date: dateRangePayload.start_date,
        end_date: dateRangePayload.end_date,
      },
      !!dateRangePayload.start_date && !!dateRangePayload.end_date
    );

  const { data: daywisePerformance, isLoading: isDaywisePerformanceLoading } =
    useGetAudienceDaywisePerformance(
      project_id,
      {
        start_date: dateRangePayload.start_date,
        end_date: dateRangePayload.end_date,
        content_id: activeCampaignGroup?.value,
        analysis_type: analysisType,
      },
      !!dateRangePayload.start_date && !!dateRangePayload.end_date
    );

  const { data: zoneWiseDistribution, isLoading: isZonalDistributionLoading } =
    useGetAudienceZonalDistribution(
      project_id,
      {
        start_date: dateRangePayload.start_date,
        end_date: dateRangePayload.end_date,
        content_id: activeCampaignGroup?.value,
      },
      !!dateRangePayload.start_date && !!dateRangePayload.end_date
    );

  const { data: screenSizeData, isLoading: isViewsByScreensizeLoading } =
    useGetAudienceViewsByScreensize(
      project_id,
      "views",
      {
        start_date: dateRangePayload.start_date,
        end_date: dateRangePayload.end_date,
        content_id: activeCampaignGroup?.value,
      },
      !!dateRangePayload.start_date && !!dateRangePayload.end_date
    );

  const { data: deviceChartData, isLoading: isDeviceDistributionLoading } =
    useGetAudienceDeviceDistribution(
      project_id,
      {
        start_date: dateRangePayload.start_date,
        end_date: dateRangePayload.end_date,
        content_id: activeCampaignGroup?.value,
      },
      !!dateRangePayload.start_date && !!dateRangePayload.end_date
    );

  const { data: specialityBreakup, isLoading: isSpecialityBreakupLoading } =
    useGetAudienceSpecialityBreakup(
      project_id,
      {
        start_date: dateRangePayload.start_date,
        end_date: dateRangePayload.end_date,
        content_id: activeCampaignGroup?.value,
      },
      !!dateRangePayload.start_date && !!dateRangePayload.end_date
    );

  const { data: specialitiesReport, isLoading: isSpecialitiesReportLoading } =
    useGetAudienceSpecialitiesReport(
      project_id,
      {
        start_date: dateRangePayload.start_date,
        end_date: dateRangePayload.end_date,
        content_id: activeCampaignGroup?.value,
      },
      !!dateRangePayload.start_date && !!dateRangePayload.end_date
    );
  const {
    data: specialitiesReportCTA,
    isLoading: isSpecialitiesReportCTALoading,
  } = useGetAudienceSpecialitiesReportCTA(
    project_id,
    {
      start_date: dateRangePayload.start_date,
      end_date: dateRangePayload.end_date,
      content_id: activeCampaignGroup?.value,
    },
    !!dateRangePayload.start_date && !!dateRangePayload.end_date
  );

  const hasViewAccess = checkProjectAccess("show_view", projectDetails);
  const hasReadAccess = checkProjectAccess("show_read", projectDetails);
  const hasCTAAccess = checkProjectAccess("show_CTA", projectDetails);

  function mergeContentData(data1, data2) {
    data1 = Array.isArray(data1) ? data1 : [];
    data2 = Array.isArray(data2) ? data2 : [];

    const data2Map = new Map(data2.map((item) => [item.speciality_name, item]));

    const allKeys = new Set([
      ...data1.flatMap(Object.keys),
      ...data2.flatMap(Object.keys),
    ]);

    const mergedData = data1.map((item) => {
      const matchingData = data2Map.get(item.name);
      const mergedItem = { ...item };

      allKeys.forEach((key) => {
        if (!(key in mergedItem)) {
          if (matchingData && key in matchingData) {
            mergedItem[key] = matchingData[key];
          } else {
            mergedItem[key] = "N/A";
          }
        }
      });

      return mergedItem;
    });

    data2.forEach((item) => {
      if (
        !mergedData.some(
          (mergedItem) => mergedItem.name === item.speciality_name
        )
      ) {
        const newItem = {
          name: item.speciality_name,
          primary_specialty: "N/A",
          views: "N/A",
          reads: "N/A",
          date_Data: "N/A",
          ...item,
        };

        allKeys.forEach((key) => {
          if (!(key in newItem)) {
            newItem[key] = "N/A";
          }
        });

        mergedData.push(newItem);
      }
    });

    return mergedData;
  }

  const tableData = mergeContentData(
    specialitiesReport,
    specialitiesReportCTA
  )?.filter((item) => {
    if (
      (hasViewAccess &&
        hasReadAccess &&
        (item.views !== 0 || item.reads !== 0)) ||
      (!hasViewAccess && item.reads !== 0) ||
      (!hasReadAccess && item.views !== 0)
    ) {
      return item;
    }
    return null;
  });

  const sponsoredContentList =
    summaryList && Array.isArray(summaryList) && summaryList.length > 0
      ? summaryList?.map((item) => {
          return {
            id: item?.content_id,
            group_title: item?.fetch_with_image[0]?.content_title,
          };
        })
      : [];

  const formatDaywiseReportData = (data) => {
    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const result = data.reduce((acc, { Day, label, Reach }) => {
      if (!acc[label]) {
        acc[label] = {
          name: analysisType === "week" ? `Week ${label}` : label,
          data: daysOfWeek.map((day) => ({ x: day, y: 0 })),
        };
      }
      const dayIndex = daysOfWeek.indexOf(Day);
      if (acc[label].data.length >= dayIndex && dayIndex >= 0) {
        acc[label].data[dayIndex].y = Reach;
      }
      return acc;
    }, {});

    return Object.values(result)
      .sort((a, b) =>
        a.name.toLocaleString().localeCompare(b.name, undefined, {
          numeric: true,
        })
      )
      .map((week) => ({
        name: week.name,
        data: week.data,
      }));
  };

  const performanceDistribution = {
    series:
      daywisePerformance && daywisePerformance?.length > 0
        ? formatDaywiseReportData(daywisePerformance)
        : [],
  };

  const zonalChartOptions =
    zoneWiseDistribution && zoneWiseDistribution?.length > 0
      ? transformHeatMapData(zoneWiseDistribution, "name", "total_count")
      : [];

  const screenSizeChartOptions = {
    label:
      screenSizeData && screenSizeData?.length > 0
        ? transformGraphData(
            screenSizeData,
            "impression_percentage",
            "screen_ratio"
          )?.label
        : [],
    series:
      screenSizeData && screenSizeData?.length > 0
        ? transformGraphData(
            screenSizeData,
            "impression_percentage",
            "screen_ratio"
          )?.series
        : [],
  };

  const deviceChartOptions = {
    series:
      deviceChartData?.length > 0
        ? transformGraphData(
            deviceChartData,
            "percentagebrowser_count",
            "Device"
          )?.series
        : [],
    label:
      deviceChartData?.length > 0
        ? transformGraphData(
            deviceChartData,
            "percentagebrowser_count",
            "Device"
          )?.label
        : [],
  };

  const userSpecialtyChartOptions = {
    series:
      specialityBreakup && specialityBreakup?.length > 0
        ? specialityBreakup?.map((item) => item?.user_speciality_breakup)
        : [],
    label:
      specialityBreakup && specialityBreakup?.length > 0
        ? specialityBreakup?.map((item) => item?.name)
        : [],
  };

  const tableCsvFormat = {
    header: ["Specialities", "Viewed", "Reads"],
    column: ["name", "views", "reads"],
    // chart: {
    //   header: "Trends",
    //   column: "date_Data",
    // },
    rows: specialitiesReport,
  };

  const tableRef = useRef(null);

  const handleTableCsvDownload = () => {
    var tableCsvFormat = {
      header: ["Specialities"],
      column: ["name"],
      rows: specialitiesReport,
    };

    if (hasViewAccess && hasReadAccess) {
      tableCsvFormat = {
        header: ["Specialities", "Viewed", "Reads"],
        column: ["name", "views", "reads"],
        rows: specialitiesReport,
      };
    } else if (hasViewAccess && !hasReadAccess) {
      tableCsvFormat = {
        header: ["Specialities", "Viewed"],
        column: ["name", "views"],
        rows: specialitiesReport,
      };
    } else if (!hasViewAccess && hasReadAccess) {
      tableCsvFormat = {
        header: ["Specialities", "Reads"],
        column: ["name", "reads"],
        rows: specialitiesReport,
      };
    }
    if (!isProjectDetailsLoading) {
      downloadCSV(tableCsvFormat);
    }
  };

  useEffect(() => {
    if (projectDetails && projectDetails?.length > 0) {
      const today = dayjs();
      const projectEndDate = dayjs(projectDetails[0]?.project_end_date);
      let result = projectDetails[0]?.project_end_date;

      if (
        projectEndDate &&
        (projectEndDate.isAfter(today) || projectEndDate.isSame(today, "day"))
      ) {
        result = today.subtract(1, "day");
      } else {
        result = projectEndDate;
      }

      if (project?.start_date && project?.end_date && !isProjectChange) {
        setDateRangePayload({
          start_date: project?.start_date,
          end_date: project?.end_date,
        });
        dispatch(
          updateProject({
            start_date: project?.start_date,
            end_date: project?.end_date,
          })
        );
      } else {
        setDateRangePayload({
          start_date: formatLocalizedDate(
            new Date(projectDetails[0]?.project_start_date),
            "YYYY-MM-DD[T]00:00:00.000Z"
          ),
          end_date: formatLocalizedDate(
            new Date(result),
            "YYYY-MM-DD[T]23:59:59.000Z"
          ),
        });
        dispatch(
          updateProject({
            start_date: formatLocalizedDate(
              new Date(projectDetails[0]?.project_start_date),
              "YYYY-MM-DD[T]00:00:00.000Z"
            ),
            end_date: formatLocalizedDate(
              new Date(result),
              "YYYY-MM-DD[T]23:59:59.000Z"
            ),
          })
        );
        setIsProjectChange(false);
      }
    }
  }, [projectDetails, project_id]);

  useEffect(() => {
    const baseColumns = [
      {
        Header: "Specialities",
        columnId: 1,
        accessor: "name",
        disableFilters: true,
      },
    ];

    const viewColumn = {
      Header: "Viewed",
      columnId: 2,
      accessor: "views",
      disableFilters: true,
    };

    const readColumn = {
      Header: "Reads",
      columnId: 3,
      accessor: "reads",
      disableFilters: true,
    };

    const ctaColumn = {
      Header: "CTA",
      columnId: 4,
      accessor: "user_count",
      disableFilters: true,
    };

    const trendColumn = {
      Header: "Trends",
      columnId: 5,
      accessor: "date_Data",
      disableFilters: true,
      Cell: ({ cell: { value } }) => {
        if (value === null) value = "0,0,0,0,0,0,0";
        return (
          <SparlLineBar
            series={value && value?.length > 0 ? value.split(",") : []}
            labels={[]}
            height={50}
          />
        );
      },
    };

    let columns = [...baseColumns];
    if (hasViewAccess) {
      columns.push(viewColumn);
    }
    if (hasReadAccess) {
      columns.push(readColumn);
    }
    if (hasCTAAccess) {
      columns.push(ctaColumn);
    }
    // columns.push(trendColumn);
    columns.forEach((column, index) => {
      column.columnId = index + 1;
    });

    setTableColumnData(columns);
  }, [hasViewAccess, hasReadAccess, hasCTAAccess]);

  useEffect(() => {
    if (sponsoredContentList && sponsoredContentList?.length > 0) {
      setActiveCampaignGroup({
        value: sponsoredContentList[0].id,
        label: sponsoredContentList[0].group_title,
      });
    }
  }, [summaryList]);

  return (
    <>
      <Row>
        <Col md={12}>
          {!isProjectDetailsLoading && (
            <ProjectHeaderTopBar
              projectType="Sponsored Content"
              title="Audience"
              projectStartDate={projectDetails[0]?.project_start_date || ""}
              projectEndDate={projectDetails[0]?.project_end_date || ""}
              isProjectDetailsLoading={isProjectDetailsLoading}
              rightComponent={
                <ProjectFilters
                  title="Audience"
                  setDateRangePayload={(e) => {
                    setDateRangePayload(e);
                    if (getWeeksBetween(e.start_date, e.end_date) > 16) {
                      setAnalysisType("month");
                    } else {
                      setAnalysisType("week");
                    }
                  }}
                  isSummaryListLoading={isSummaryListLoading}
                  projectStartDate={projectDetails[0]?.project_start_date || ""}
                  projectEndDate={projectDetails[0]?.project_end_date || ""}
                  dateLabel={dateRangePayload}
                  tabs={summaryList}
                  activeSummaryTab={activeCampaignGroup}
                  setActiveSummaryTab={setActiveCampaignGroup}
                />
              }
            />
          )}
        </Col>

        <Col md={hasViewAccess ? 6 : 12} className="mb-4">
          {!isDaywisePerformanceLoading && daywisePerformance?.length > 0 ? (
            <HeatMapsChart
              series={performanceDistribution.series}
              title={"Day wise performance distribution"}
            />
          ) : isDaywisePerformanceLoading ? (
            <SquareChartSkeleton />
          ) : (
            <CardNotFound
              title={"Day wise performance distribution"}
              height={320}
            />
          )}
        </Col>

        <Col md={hasViewAccess ? 6 : 8} className="mb-4">
          {!isZonalDistributionLoading && zoneWiseDistribution?.length > 0 ? (
            <TreeMapChart
              title={"Zonal Distribution"}
              series={zonalChartOptions}
            />
          ) : isZonalDistributionLoading ? (
            <SquareChartSkeleton />
          ) : (
            <CardNotFound title={"Zonal Distribution"} height={320} />
          )}
        </Col>

        <Col md={4} sm={12} className={`mb-4 ${hasViewAccess ? "" : "d-none"}`}>
          {!isViewsByScreensizeLoading && screenSizeData?.length > 0 ? (
            <PolarChart
              labels={screenSizeChartOptions.label}
              series={screenSizeChartOptions.series}
              title={"View by Screen Size"}
              graphColor={GRAPH_FILL_COLOR}
              height={320}
            />
          ) : isViewsByScreensizeLoading ? (
            <SquareChartSkeleton />
          ) : (
            <CardNotFound title={"View by Screen Size"} height={320} />
          )}
        </Col>

        <Col md={4} sm={12} className={`mb-4 ${hasViewAccess ? "" : "d-none"}`}>
          {!isDeviceDistributionLoading && deviceChartData?.length > 0 ? (
            <DonutChart
              series={deviceChartOptions.series}
              labels={deviceChartOptions.label}
              title={"View by Device"}
              graphColor={GRAPH_FILL_COLOR}
              height={320}
            />
          ) : isDeviceDistributionLoading ? (
            <SquareChartSkeleton />
          ) : (
            <CardNotFound title={"View by Device"} height={320} />
          )}
        </Col>

        <Col md={4} sm={12} className="mb-4">
          {!isSpecialityBreakupLoading && specialityBreakup?.length > 0 ? (
            <PieChart
              graphColor={GRAPH_FILL_COLOR}
              series={userSpecialtyChartOptions.series}
              labels={userSpecialtyChartOptions.label}
              title={"User Specialty Breakup"}
              height={320}
            />
          ) : isSpecialityBreakupLoading ? (
            <SquareChartSkeleton />
          ) : (
            <CardNotFound title={"User Specialty Breakup"} height={320} />
          )}
        </Col>

        <Col md={12} sm={12}>
          <Card>
            <TableHeader
              title="Specialities"
              downloadProps={{
                needCsvDownload:
                  !isProjectDetailsLoading &&
                  !isSpecialitiesReportLoading &&
                  Array.isArray(specialitiesReport) &&
                  specialitiesReport?.length > 0,
                needImgDownload:
                  !isProjectDetailsLoading &&
                  !isSpecialitiesReportLoading &&
                  Array.isArray(specialitiesReport) &&
                  specialitiesReport?.length > 0,
                exportCSVHandler: handleTableCsvDownload,
                downloadImageHandler: () => downloadTablePNG(tableRef),
              }}
            />

            <Card.Body>
              <div ref={tableRef}>
                {" "}
                {!isSpecialitiesReportLoading &&
                Array.isArray(specialitiesReport) &&
                specialitiesReport.length > 0 ? (
                  <ReactTable
                    data={tableData}
                    columns={tableColumnData}
                    isPaginationShow={false}
                    bordered
                    noDataText="no data"
                    noDataImage={fallbackImages.noActionData}
                  />
                ) : isSpecialitiesReportLoading ? (
                  <CommonTableLoader tableRowLength={5} />
                ) : (
                  <CardNotFound />
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}

export default Audience;
